/* ----------------------------------------------------------------------------
 * Support (tickets) — data (fetch) layer. Thin, typed, workspace-scoped.
 *
 * Workspace scoping is SERVER-authoritative: every call rides the cookie session
 * (credentials:'include') and the API derives the workspace from it — the same
 * model as the rest of src/data/*.ts. Mutations carry the CSRF double-submit +
 * idempotency headers automatically (see lib/api.ts).
 *
 * The Support module ships DARK behind an owner-gated DDL, so any of these calls
 * can legitimately reject with 503 { code: 'support_unprovisioned' }. Callers use
 * isUnprovisioned() (support-model.ts) to render the calm "not enabled" state.
 * -------------------------------------------------------------------------- */

import { api } from '@/lib/api';
import {
  SUPPORT_ENDPOINTS,
  type TicketDetailResponse,
  type TicketDto,
  type TicketListResponse,
  type TicketStatus,
} from './support-model';

/** GET /v1/support/tickets[?status=] — this workspace's tickets, newest first. */
export async function listTickets(status?: TicketStatus, signal?: AbortSignal): Promise<TicketDto[]> {
  const res = await api.get<TicketListResponse>(
    SUPPORT_ENDPOINTS.tickets,
    status ? { status } : undefined,
    signal,
  );
  return res?.tickets ?? [];
}

/** GET /v1/support/tickets/{id} — one ticket + its full staff thread. */
export function getTicket(id: number, signal?: AbortSignal): Promise<TicketDetailResponse> {
  return api.get<TicketDetailResponse>(SUPPORT_ENDPOINTS.ticket(id), undefined, signal);
}

export interface CreateTicketInput {
  subject: string;
  body?: string;
  priority?: string;
  contact_id?: number;
}

/** POST /v1/support/tickets — staff-create a ticket. */
export function createTicket(input: CreateTicketInput): Promise<{ ok: boolean; ticket: TicketDto }> {
  return api.post<{ ok: boolean; ticket: TicketDto }>(SUPPORT_ENDPOINTS.tickets, input);
}

/** POST /v1/support/tickets/{id}/messages — staff reply (or internal note). */
export function replyToTicket(
  id: number,
  body: string,
  isInternal = false,
): Promise<{ ok: boolean; message_id: number; status: TicketStatus }> {
  return api.post(SUPPORT_ENDPOINTS.messages(id), { body, is_internal: isInternal });
}

/** POST /v1/support/tickets/{id}/status — explicit status transition. */
export function changeTicketStatus(
  id: number,
  status: TicketStatus,
  reason?: string,
): Promise<{ ok: boolean; previous_status: TicketStatus; status: TicketStatus }> {
  return api.post(SUPPORT_ENDPOINTS.status(id), { status, reason });
}
